import React from "react"

import PropTypes from "prop-types"

import "./index.less"
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
  LabelList,
} from "recharts"
import { Button } from "antd"
import { browserSize, validate } from "../../../../utils/functions"
import CustomizedAxisTick from "./components/CustomizedAxisTick"
import CustomTooltip from "./components/CustomTooltip"
import { isAmbassadorActive, isModuleBySlug } from "../../../../utils/storage"
import { BENCHMARK, SURVEY_CALCULATION_TYPE } from "../../../../utils/constants"
import { loadTexts } from "../../../../utils/i18n"

const toPercent = (decimal) => {
  return `${decimal.toFixed(0)}%`
}

const renderCustomizedLabel = (props, auxData, idSurveyCalculationType) => {
  const { x, y, width, name, value, index, id } = props
  const radius = 10
  const currentData = auxData[index]
  if (currentData.anonymity_check) {
    if (!currentData.anonymity_check.valid) {
      return null
    }
  }

  const value_visible = currentData.value_visible
  return (
    <g>
      <text
        x={x + width / 2}
        y={y - radius}
        fill={id ? "#3482D7" : "#55C8AE"}
        fontSize={12}
        textAnchor="middle"
        dominantBaseline="middle">
        {idSurveyCalculationType === 1
          ? `${Math.round(value)}%`
          : `${value_visible}`}
      </text>
    </g>
  )
}

export function ResultByDimensionChart(props) {
  const intl = loadTexts("reports_general_view")
  const intlDimensionChart =
    intl.reportAmbassador.engagementMeasurement
      .widgetResultByDimensionEngagement.resultByDimensionChart
  const widthChart = props.data.length * 145
  const sizeBrowser = browserSize()
  const styleChart =
    sizeBrowser.width - 135 < widthChart ? { width: `${widthChart}px` } : null
  const isBenchmark = isModuleBySlug(BENCHMARK)
  const validateAmbassador = isAmbassadorActive()
    ? props.measureGeneral.ambassador_benchmark_visible
    : true

  const isCalculationTopTwoBox =
    props.measureGeneral.id_survey_calculation_type ===
    SURVEY_CALCULATION_TYPE.topTwoBox

  const getColor = (entry) => {
    if (entry.anonymity_check) {
      if (!entry.anonymity_check.valid) {
        return "#C4C4C4"
      }
    }

    // return entry.name === "Resultado total" ? "#55C8AE" : "#3482D7"
    return entry.id_dimension === 0 ? "#55C8AE" : "#3482D7"
  }
  const auxData = props.data

  return (
    <div className="ResultByDimensionChart" style={styleChart}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          height: "32px",
        }}>
        {validate.exist(props.dimensionParent) && props.dimensionParent != 0 ? (
          <Button
            className="ResultByDimensionChart-button"
            type="primary"
            onClick={() => props.getBackData(props.dimensionParent)}>
            {intlDimensionChart.buttonBack}
          </Button>
        ) : null}
      </div>
      <ResponsiveContainer>
        <ComposedChart
          data={props.data}
          margin={{
            top: 28,
            right: 0,
            bottom: 0,
            left: 0,
          }}>
          <CartesianGrid vertical={false} stroke="#EDE9E9" />
          <XAxis
            axisLine={false}
            tickLine={false}
            tickMargin={10}
            height={90}
            dataKey="index"
            type="category"
            padding={{ left: 40, right: 40 }}
            tick={(element) => (
              <CustomizedAxisTick
                element={element}
                data={props.data}
                hideImpact={props.hideImpact}
              />
            )}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickCount={6}
            type="number"
            tick={{ fill: "#8D8A8D", fontSize: "12" }}
            domain={
              props.measureGeneral.id_survey_calculation_type === 1
                ? [0, 100]
                : [0, 5]
            }
            tickFormatter={
              props.measureGeneral.id_survey_calculation_type === 1 && toPercent
            }
          />
          <Tooltip
            content={(element) => (
              <CustomTooltip
                element={element}
                measureGeneral={props.measureGeneral}
              />
            )}
            cursor={{ fill: "transparent" }}
          />
          <Legend verticalAlign="top" height={36} wrapperStyle={{ top: 0 }} />
          <Bar
            name={intlDimensionChart.dimensionBar}
            dataKey={isCalculationTopTwoBox ? "value" : "value_visible"}
            barSize={52}
            fill="#3482D7"
            onClick={(data) => props.getDataChildren(data)}
            radius={[4, 4, 4, 4]}>
            <LabelList
              dataKey={isCalculationTopTwoBox ? "value" : "value_visible"}
              position="top"
              fontSize={10}
              fill="#3482D7"
              content={(innerProps) =>
                renderCustomizedLabel(
                  innerProps,
                  auxData,
                  props.measureGeneral.id_survey_calculation_type
                )
              }
            />
            {props.data.map((entry, index) => {
              return <Cell key={`cell-${index}`} fill={getColor(entry)} />
            })}
          </Bar>
          {props.isAmbassador && (
            <Line
              name={intlDimensionChart.organizationBar}
              type="linear"
              dataKey="value_org"
              stroke="#56CCF2"
              fill="#56CCF2"
              strokeDasharray="5 5"
              connectNulls
              dot={{ stroke: "#56CCF2", strokeDasharray: "" }}
              activeDot={{ strokeDasharray: "", r: 3.5 }}
            />
          )}
          {props.showDimensionBenchmark &&
            isBenchmark &&
            validateAmbassador && (
              <Line
                name={intlDimensionChart.benchmarkBar}
                type="linear"
                dataKey="benchmark_value"
                stroke="#F6B673"
                fill="#F6B673"
                strokeDasharray="5 5"
                connectNulls
                dot={{ stroke: "#F6B673", strokeDasharray: "" }}
                activeDot={{ strokeDasharray: "", r: 3.5 }}
              />
            )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

ResultByDimensionChart.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  measureGeneral: PropTypes.object,
  getDataChildren: PropTypes.func,
  getBackData: PropTypes.func,
  dimensionParent: PropTypes.number,
  isAmbassador: PropTypes.bool,
  showDimensionBenchmark: PropTypes.bool,
  hideImpact: PropTypes.bool,
}
export default ResultByDimensionChart
